/**
 * Payment Method Selection Screen - Tela 1
 *
 * Displays saved cards (if any) and allows selection between saved card or new card entry.
 */

import { useState } from 'react';
import type { SavedCard } from '@/types/voice-dtmf.types';
import { CardTypeBadge } from '../components/CardTypeBadge';
import { AmountDisplay } from '../components/AmountDisplay';
import styles from './PaymentMethodSelection.module.css';

interface PaymentMethodSelectionProps {
  amount: number;
  currency: string;
  savedCards?: SavedCard[];
  onSelectPaymentMethod: (
    method: 'saved-card' | 'new-card',
    cardId?: string
  ) => void;
  onDeleteCard: (card: SavedCard) => void;
  isProcessing?: boolean;
  onCancel?: () => void;
  cancelDisabled?: boolean;
}

export const PaymentMethodSelection = ({
  amount,
  currency,
  savedCards = [],
  onSelectPaymentMethod,
  onDeleteCard,
  isProcessing = false,
  onCancel,
  cancelDisabled,
}: PaymentMethodSelectionProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
  };

  const handleContinue = () => {
    if (!selectedOption) return;

    if (selectedOption === 'new-card') {
      onSelectPaymentMethod('new-card');
    } else {
      // selectedOption is a cardId
      onSelectPaymentMethod('saved-card', selectedOption);
    }
  };

  return (
    <div className={styles.container}>
      {/* Amount Display */}
      <div className={styles.section}>
        <div className={styles.optionsSection}>
          <AmountDisplay
            amount={amount}
            currency={currency}
            onCancel={onCancel}
            cancelDisabled={cancelDisabled}
          />

          {/* Heading */}
          <h2 className={styles.heading}>Choose a card option to proceed:</h2>

          {/* Payment Options */}
          <div className={styles.optionsContainer}>
            {/* Saved Cards */}
            {savedCards.map((card) => {
              const isExpired = card.expiryStatus === 'EXPIRED';
              const isExpiring = card.expiryStatus === 'EXPIRING';

              return (
                <div
                  key={card.cardId}
                  className={`${styles.optionCard} ${
                    selectedOption === card.cardId ? styles.selected : ''
                  } ${isExpired ? styles.expired : ''}`}
                >
                  <input
                    type="radio"
                    id={`card-${card.cardId}`}
                    name="payment-method"
                    value={card.cardId}
                    checked={selectedOption === card.cardId}
                    onChange={(e) => handleOptionChange(e.target.value)}
                    className={styles.radio}
                    disabled={isExpired}
                  />
                  <label
                    htmlFor={`card-${card.cardId}`}
                    className={styles.cardLabel}
                  >
                    <div className={styles.cardDetails}>
                      <div className={styles.cardName}>{card.cardName}</div>
                      <div className={styles.cardNumber}>
                        Ending in {card.maskedCardNumber.slice(-4)}
                      </div>
                    </div>
                    <div className={styles.cardMeta}>
                      <div className={styles.cardLogo}>
                        <CardTypeBadge cardType={card.cardType} size="small" />
                      </div>
                      <div className={styles.cardExpiry}>
                        {isExpired ? (
                          <span className={styles.expiredText}>
                            Expired on {card.expiryDate}
                          </span>
                        ) : isExpiring ? (
                          <span className={styles.expiringText}>
                            Expires on {card.expiryDate}
                          </span>
                        ) : (
                          <span>{card.expiryDate}</span>
                        )}
                      </div>
                    </div>
                  </label>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCard(card);
                    }}
                    aria-label={`Delete ${card.cardName}`}
                  >
                    <svg
                      width="14"
                      height="18"
                      viewBox="0 0 14 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.25022 0C9.769 4.32195e-08 11.0002 1.23122 11.0002 2.75V3.19238C11.7954 3.26939 12.5841 3.3694 13.3655 3.49121C13.7747 3.555 14.0551 3.93847 13.9914 4.34766C13.9276 4.75685 13.5442 5.0373 13.135 4.97363C13.0856 4.96593 13.036 4.95771 12.9865 4.9502L12.1448 15.4697C12.0302 16.8987 10.8371 18 9.40354 18H4.5969C3.16331 18 1.97024 16.8987 1.85569 15.4697L1.01389 4.9502C0.964557 4.9577 0.914728 4.96595 0.865454 4.97363C0.456346 5.03717 0.0727919 4.75678 0.00900916 4.34766C-0.0546426 3.93848 0.225799 3.555 0.634986 3.49121C1.41637 3.3694 2.20501 3.2694 3.00022 3.19238V2.75C3.00022 1.23122 4.23144 0 5.75022 0H8.25022ZM4.80002 6.00098C4.38629 6.01769 4.06472 6.36649 4.08127 6.78027L4.38108 14.2803C4.39779 14.694 4.74659 15.0156 5.16038 14.999C5.57421 14.9824 5.89666 14.6336 5.8801 14.2197L5.57932 6.71973C5.5626 6.30599 5.2138 5.98442 4.80002 6.00098ZM9.20042 6.00098C8.78663 5.98443 8.43783 6.30599 8.42112 6.71973L8.12131 14.2197C8.10476 14.6335 8.42632 14.9823 8.84006 14.999C9.25384 15.0156 9.60264 14.694 9.61936 14.2803L9.92014 6.78027C9.9367 6.36644 9.61423 6.01761 9.20042 6.00098ZM5.75022 1.5C5.05986 1.5 4.50022 2.05964 4.50022 2.75V3.0752C5.32725 3.02543 6.16083 3 7.00022 3C7.83958 3 8.67322 3.02544 9.50022 3.0752V2.75C9.50022 2.05964 8.94058 1.5 8.25022 1.5H5.75022Z"
                        fill="#71716F"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}

            {/* New Card Option */}
            <div
              className={`${styles.optionCard} ${
                selectedOption === 'new-card' ? styles.selected : ''
              }`}
            >
              <input
                type="radio"
                id="new-card"
                name="payment-method"
                value="new-card"
                checked={selectedOption === 'new-card'}
                onChange={(e) => handleOptionChange(e.target.value)}
                className={styles.radio}
              />
              <label htmlFor="new-card" className={styles.newCardLabel}>
                <span className={styles.newCardText}>Pay with a new card</span>
                <div className={styles.newCardIcon}>
                  <svg
                    width="21"
                    height="17"
                    viewBox="0 0 21 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.75 4.5H20.25M0.75 5.25H20.25M3.75 10.5H9.75M3.75 12.75H6.75M3 15.75H18C19.2426 15.75 20.25 14.7426 20.25 13.5V3C20.25 1.75736 19.2426 0.75 18 0.75H3C1.75736 0.75 0.75 1.75736 0.75 3V13.5C0.75 14.7426 1.75736 15.75 3 15.75Z"
                      stroke="#3A3A36"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </label>
            </div>
          </div>
        </div>
        {/* Continue Button */}
        <button
          type="button"
          className={styles.continueButton}
          disabled={!selectedOption || isProcessing}
          onClick={handleContinue}
        >
          {isProcessing
            ? 'Processing...'
            : selectedOption === 'new-card'
              ? 'Continue with new card'
              : 'Continue with this card'}
        </button>
      </div>
    </div>
  );
};
